import React, { useEffect, useState } from 'react';
import TextArea from 'components/TextArea/TextArea';
import DefaultModal from 'components/Modal/DefaultModal';
import Button from 'components/Button/Button';
import { useAppDispatch, useAppSelector } from 'helpers/redux';
import { useForm } from 'react-hook-form';
import { FileAttachment } from 'types';
import { RootState } from 'app/store';
import { isObjectEmpty } from 'helpers/checkEmptiness';
import { HttpClientError, RequestStatus } from 'http-client';
import {
    customWarnOption,
    deletedStatusOption,
    savedStatusOption,
} from 'helpers/alertLabel';
import withReactContent from 'sweetalert2-react-content';
import Swal, { SweetAlertIcon } from 'sweetalert2';
import FileDropzone from 'components/FileInput/Dropzone';
import JobFile from 'features/worksheet/process/job/components/JobFile';
import { Alert } from 'flowbite-react';
import { HiInformationCircle } from 'react-icons/hi';
import {
    darkGreen,
    darkRed,
    JOB_STATUS_HOLD_CE,
} from '../../../../../constants';
import {
    readyToQCMRE,
    deleteMREFile,
    getMREFile,
    reset,
    setUploadProgress,
    updateMRENotes,
    uploadMREFile,
    completeMRE,
    getQCMREFile,
    updateQCMRENotes,
    uploadQCMREFile,
    deleteQCMREFile,
} from '../mre.reducer';
import FileProgress from '../../../../basic/fileAttachment/components/FileProgress';
import { JobID } from '../../jobDetail/models';
import { InputMRE } from '../models';
import { getSingleMRE } from '../../../process/mre/processMRE.reducer';
import { MREModel } from '../../../process/mre/models';
import { UserCredential } from '../../../../login/models';
import { checkRoleActionAccess } from '../../../../../routes/checkRoleAccess';
import { ActionEnum, MenuEnum } from '../../../../../routes/models';
import { changeStatusJob } from 'features/worksheet/process/job/job.reducer';
import { useNavigate } from 'react-router-dom';
import { BaseServiceClass } from 'base-service-class';
import { Button as FlowbiteButton } from 'flowbite-react';

const DetailQCMREForm = ({ jobId = undefined }: DetailQCMREFormProps) => {
    let navigate = useNavigate();
    const { register, handleSubmit, reset: resetForm } = useForm<InputMRE>();

    const dispatch = useAppDispatch();
    const MySwal = withReactContent(Swal);

    const [showAttachmentModal, setShowAttachmentModal] =
        useState<boolean>(false);
    const [currentFiles, setCurrentFiles] = useState<Array<FileAttachment>>([]);
    const [totalFileRemoved, setTotalFileRemoved] = useState<number>(0);
    const [localFiles, setLocalFiles] = useState<Array<File>>([]);
    const [showUploadModal, setShowUploadModal] = useState<boolean>(false);

    const user = useAppSelector(
        (state: RootState) => state.user.userAuth ?? ({} as UserCredential)
    );
    const singleMRE = useAppSelector(
        (state: RootState) => state.processMRE.singleMRE ?? ({} as MREModel)
    );
    const mreFile = useAppSelector(
        (state: RootState) => state.mre.jobQCMREFile ?? []
    );
    const uploadStatus = useAppSelector(
        (state: RootState) => state.mre.uploadStatus ?? []
    );
    const uploadProgresses = useAppSelector(
        (state: RootState) => state.mre.uploadProgress ?? []
    );
    const deleteFileStatus = useAppSelector(
        (state: RootState) => state.mre.deleteQCFileStatus
    );
    const status = useAppSelector((state: RootState) => state.mre.status);
    const error = useAppSelector(
        (state: RootState) => state.mre.error ?? ({} as HttpClientError)
    );

    useEffect(() => {
        if (jobId && jobId !== '') {
            dispatch(getQCMREFile(jobId as string));
            dispatch(getSingleMRE(jobId as string));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [jobId]);

    useEffect(() => {
        if (!isObjectEmpty(singleMRE)) {
            resetForm({ notes: singleMRE.mre_qc_notes });
        }
    }, [singleMRE]);

    useEffect(() => {
        if (status !== undefined && status !== RequestStatus.pending) {
            MySwal.fire(
                savedStatusOption(
                    status === RequestStatus.success,
                    error.data?.message
                )
            ).then(() => {
                dispatch(reset());
                dispatch(setUploadProgress([]));
                // return navigate('/worksheet/process/job-prep', {replace: true});
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [status, error]);

    useEffect(() => {
        if (
            deleteFileStatus !== undefined &&
            deleteFileStatus !== RequestStatus.pending
        ) {
            MySwal.fire(
                deletedStatusOption(
                    deleteFileStatus === RequestStatus.success,
                    error.data?.message
                )
            ).then(() => {
                dispatch(reset());
                dispatch(getQCMREFile(jobId as string));
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [deleteFileStatus, error]);

    const onModalAttachmentClose = () => {
        setLocalFiles([]);
        dispatch(reset());
        dispatch(getQCMREFile(jobId as string));
        setShowAttachmentModal(false);
    };

    const onFileChange = (files: Array<File>) => {
        setLocalFiles(files);
    };

    const onFileRemove = (fileId: string) => {
        let newFiles = currentFiles.filter((f) => f.id !== fileId);
        setCurrentFiles(newFiles);
        setTotalFileRemoved((prevState) => prevState + 1);
    };

    const onSubmit = (data: InputMRE) => {
        dispatch(
            updateQCMRENotes({
                job_id: jobId as string,
                mre_qc_notes: data.notes,
            })
        );
    };

    const onHoldClicked = () => {
        MySwal.fire({
            title: <span>Hold CE job!</span>,
            html: (
                <div>
                    <span>
                        Are you sure? You will not be able to recover this data!
                    </span>
                    <br />
                    <br />
                    <span>
                        Please fill out the Form Report After Holding the Job
                    </span>
                    <br />
                    <a
                        href={`${BaseServiceClass.DOMAIN}/bill-job/create-bill-job/hold-ce/${jobId}`}
                        target="_blank"
                        rel="noreferrer"
                        className="text-blue-700"
                    >
                        {BaseServiceClass.DOMAIN}
                        /bill-job/create-bill-job/hold-ce/{jobId}
                    </a>
                </div>
            ),
            icon: 'warning',
            showCancelButton: true,
            cancelButtonColor: darkGreen,
            confirmButtonText: 'Yes',
        }).then((result) => {
            if (result.isConfirmed) {
                // dispatch(completeJob({ job_id: jobId as string }));
                dispatch(
                    changeStatusJob({
                        job_id: jobId as string,
                        status: JOB_STATUS_HOLD_CE,
                        userId: user.user_id,
                    })
                ).then((res: any) => {
                    if (res.payload.status) {
                        // redirect to QC page after show success alert

                        MySwal.fire({
                            title: 'Job on Hold CE',
                            text: 'The job has been put on Hold CE status.',
                            icon: 'success' as SweetAlertIcon,
                        }).then(() => {
                            // redirect to QC page
                            navigate(
                                `/bill-job/create-bill-job/hold-ce/${jobId}`,
                                {
                                    replace: true,
                                }
                            );
                        });
                    }
                    console.log('Hold CE Status: ', status);
                });
            }
        });
    };

    console.log('singleMRE: ', singleMRE);

    const onCompleteClicked = () => {
        MySwal.fire({
            title: <span>Complete MRE job!</span>,
            html: (
                <div>
                    <span>
                        Are you sure? You will not be able to recover this data!
                    </span>
                    {singleMRE.status_name !== 'Hold CE' && (
                        <>
                            <br />
                            <br />
                            <span>Form Report</span>
                            <br />
                            <a
                                href={`${BaseServiceClass.DOMAIN}/bill-job/create-bill-job/history/${jobId}`}
                                target="_blank"
                                rel="noreferrer"
                                className="text-blue-700"
                            >
                                {BaseServiceClass.DOMAIN}
                                /bill-job/create-bill-job/history/{jobId}
                            </a>
                        </>
                    )}
                </div>
            ),
            icon: 'warning',
            showCancelButton: true,
            cancelButtonColor: darkGreen,
            confirmButtonText: 'Yes',
        }).then((result) => {
            if (result.isConfirmed) {
                dispatch(completeMRE(jobId as string));
            }
        });
    };

    const onFileDelete = (fileId: string) => {
        MySwal.fire({
            text: `Are you sure you want to delete this file?`,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: darkRed,
            cancelButtonColor: darkGreen,
            confirmButtonText: 'Yes, delete it!',
        }).then((result) => {
            if (result.isConfirmed) {
                dispatch(deleteQCMREFile(fileId));
            }
        });
    };

    const onUploadModalClose = () => {
        setShowUploadModal(false);
        onModalAttachmentClose();
    };

    const toggleUploadModal = (args: boolean) => {
        setShowUploadModal(args);
        onModalAttachmentClose();
    };

    const QCMREFileAttachment = () => {
        const onUpload = () => {
            if (localFiles.length === 0) {
                MySwal.fire({
                    title: 'File Empty!',
                    text: "You haven't selected any file",
                    icon: 'warning' as SweetAlertIcon,
                });
            } else {
                setShowUploadModal(true);
                dispatch(
                    uploadQCMREFile({
                        job_id: jobId as string,
                        local_files: localFiles,
                    })
                );
            }
        };

        return (
            <div className="p-2 md:p-6 pt-2 md:pt-2 bg-white dark:border-gray-700 dark:bg-gray-800 rounded-2xl">
                <FileDropzone
                    label="Attachment"
                    onChange={onFileChange}
                    uploadProgress={uploadProgresses}
                    files={localFiles}
                />

                {totalFileRemoved > 0 && (
                    <div className="mt-3">
                        <Alert color="warning" icon={HiInformationCircle}>
                            <span>
                                Changes made in this file list will not be saved
                                until the form is submitted
                            </span>
                        </Alert>
                    </div>
                )}

                <div className="flex justify-center my-2">
                    <div className="mx-1">
                        <Button
                            title="Cancel"
                            type="button"
                            onClick={onModalAttachmentClose}
                        />
                    </div>
                    <div className="mx-1">
                        <Button title="Save" type="button" onClick={onUpload} />
                    </div>
                </div>
            </div>
        );
    };

    return (
        <form onSubmit={handleSubmit(onSubmit)}>
            <div>
                <TextArea
                    label="QC MRE Notes"
                    placeholder="Input QC MRE Notes"
                    {...register('notes')}
                />
                <div className="my-3">
                    <div className="mr-2 flex-grow">
                        {checkRoleActionAccess(
                            user?.roleName,
                            MenuEnum.WorksheetQCMRE,
                            ActionEnum.Attachment
                        ) && (
                            <Button
                                title="Add/Remove Attachment"
                                type="button"
                                onClick={() => setShowAttachmentModal(true)}
                            />
                        )}
                    </div>
                </div>
                <div>
                    <JobFile
                        files={currentFiles}
                        uploadedFile={mreFile}
                        onFileRemove={onFileRemove}
                        onFileDelete={onFileDelete}
                    />
                </div>
            </div>
            <div className="mt-3 grid md:grid-cols-1 gap-4">
                <div className="flex flex-grow justify-end">
                    <div className="mr-2">
                        {checkRoleActionAccess(
                            user?.roleName,
                            MenuEnum.WorksheetQCMRE,
                            ActionEnum.Edit
                        ) && <Button title="Save" type="submit" />}
                    </div>
                    {checkRoleActionAccess(
                        user?.roleName,
                        MenuEnum.WorksheetQCMRE,
                        ActionEnum.CompleteMRE
                    ) && (
                        <div className="inline mr-2">
                            <Button
                                title="Hold CE"
                                type="button"
                                onClick={onHoldClicked}
                            />
                        </div>
                    )}
                    <div className="inline">
                        {checkRoleActionAccess(
                            user?.roleName,
                            MenuEnum.WorksheetQCMRE,
                            ActionEnum.CompleteMRE
                        ) && (
                            <FlowbiteButton
                                type="button"
                                color="success"
                                onClick={onCompleteClicked}
                            >
                                COMPLETE MRE
                            </FlowbiteButton>
                        )}
                    </div>
                </div>
            </div>
            <DefaultModal
                title="MRE Attachment"
                show={showAttachmentModal}
                onClose={onModalAttachmentClose}
                body={<QCMREFileAttachment />}
            />
            <DefaultModal
                title="Uploading File"
                show={showUploadModal}
                onClose={() => onUploadModalClose()}
                body={
                    <FileProgress
                        uploadStatus={uploadStatus}
                        uploadProgress={uploadProgresses}
                        setShowModal={toggleUploadModal}
                    />
                }
            />
        </form>
    );
};

export interface DetailQCMREFormProps {
    jobId?: string;
}

export default DetailQCMREForm;
